import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import ModalComponent from 'components/common/ModalComponent'
import { createDefaultDateRange, DATE_FILTER_TYPES, DATE_PRESETS_BY_TYPE } from 'utils/dateRangeFilters'

const selectClassName = 'p-2 pr-10 border rounded-md shadow-sm bg-white font-normal appearance-none w-full'

const getSectorId = sector => sector.blockGroupId ?? sector.groupId
const getGroupId = group => group.deviceGroupId ?? group.groupId
const getActivityId = activity => activity.activityId ?? activity.id

const SelectField = ({ label, name, value, onChange, children }) => (
  <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
    {label}
    <span className='relative'>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={selectClassName}
      >
        {children}
      </select>
      <ChevronDownIcon className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
    </span>
  </label>
)

export default function DateRangeFilterModal ({
  isOpen,
  dateRange,
  sectors = [],
  farmingBlocks = [],
  groups = [],
  activities = [],
  maxDate,
  title,
  applyLabel,
  showClearFilters = true,
  isSubmitting = false,
  onClose,
  onApply
}) {
  const { t } = useTranslation()
  const shownTitle = title || t('dateFilter.title')
  const shownApplyLabel = applyLabel || t('dateFilter.apply')
  const getPresetOptions = (dateFilterType) => DATE_PRESETS_BY_TYPE[dateFilterType] || DATE_PRESETS_BY_TYPE.custom
  const handleClearFilters = () => {
    onApply(createDefaultDateRange(maxDate || dateRange.dateFrom))
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={shownTitle}
      initialData={dateRange}
      onSave={onApply}
      maxWidth='lg'
      customFooter={({ formData }) => (
        <>
          {showClearFilters && (
            <button
              type='button'
              onClick={handleClearFilters}
              className='px-4 py-2 rounded bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            >
              {t('dateFilter.clearFilters')}
            </button>
          )}
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            {t('dateFilter.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting || (formData.dateFilterType === 'custom' && (!formData.dateFrom || !formData.dateTo))}
            className='px-4 py-2 rounded bg-greenPrimary text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {isSubmitting ? t('dateFilter.downloading') : shownApplyLabel}
          </button>
        </>
      )}
    >
      {({ formData, handleChange }) => {
        const presetOptions = getPresetOptions(formData.dateFilterType)
        const selectedPreset = presetOptions.some(option => option.value === formData.datePreset)
          ? formData.datePreset
          : presetOptions[0]?.value
        const showDateRange = formData.dateFilterType === 'custom'
        const showWeekNumber = formData.dateFilterType === 'week' && selectedPreset === 'week_number'
        const handlePeriodChange = event => {
          handleChange(event)
          const nextPreset = getPresetOptions(event.target.value)[0]?.value || 'custom'
          handleChange({ target: { name: 'datePreset', value: nextPreset } })
        }
        const handleFilterSelectChange = (event, labelName) => {
          handleChange(event)
          handleChange({
            target: {
              name: labelName,
              value: event.target.value ? event.target.selectedOptions[0]?.text || '' : ''
            }
          })
        }

        return (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <SelectField
              label={t('dateFilter.period')}
              name='dateFilterType'
              value={formData.dateFilterType}
              onChange={handlePeriodChange}
            >
              {DATE_FILTER_TYPES.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </SelectField>

            <SelectField
              label={t('dateFilter.searchRange')}
              name='datePreset'
              value={selectedPreset}
              onChange={handleChange}
            >
              {presetOptions.map(option => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </SelectField>

            {showDateRange && (
              <>
                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  {t('dateFilter.from')}
                  <input
                    type='date'
                    name='dateFrom'
                    value={formData.dateFrom}
                    max={maxDate}
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>

                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  {t('dateFilter.to')}
                  <input
                    type='date'
                    name='dateTo'
                    value={formData.dateTo}
                    min={formData.dateFrom}
                    max={maxDate}
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>
              </>
            )}

            {showWeekNumber && (
              <>
                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  {t('dateFilter.weekNumber')}
                  <input
                    type='number'
                    name='weekNumber'
                    value={formData.weekNumber}
                    min='1'
                    max='53'
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>

                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  {t('dateFilter.year')}
                  <input
                    type='number'
                    name='weekYear'
                    value={formData.weekYear}
                    min='2000'
                    max='2100'
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>
              </>
            )}

            <SelectField
              label={t('dateFilter.group')}
              name='sectorId'
              value={formData.sectorId}
              onChange={event => handleFilterSelectChange(event, 'blockGroupName')}
            >
              <option value=''>{t('dateFilter.allGroups')}</option>
              {sectors.map(sector => {
                const sectorId = getSectorId(sector)
                return (
                  <option key={`group-${sectorId}`} value={sectorId}>
                    {sector.groupName}
                  </option>
                )
              })}
              {farmingBlocks.length > 0 && (
                <optgroup label={t('dateFilter.greenhouses')}>
                  {farmingBlocks.map(block => (
                    <option key={`block-${block.farmingBlockId}`} value={block.farmingBlockId}>
                      {block.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </SelectField>

            <SelectField
              label={t('dateFilter.deviceGroup')}
              name='groupId'
              value={formData.groupId}
              onChange={event => handleFilterSelectChange(event, 'deviceGroupName')}
            >
              <option value=''>{t('dateFilter.allDeviceGroups')}</option>
              {groups.map(group => {
                const groupId = getGroupId(group)
                return (
                  <option key={groupId} value={groupId}>
                    {group.groupName}
                  </option>
                )
              })}
            </SelectField>

            <SelectField
              label={t('dateFilter.activity')}
              name='activityId'
              value={formData.activityId || ''}
              onChange={event => handleFilterSelectChange(event, 'activityName')}
            >
              <option value=''>{t('dateFilter.allActivities')}</option>
              {activities.map(activity => {
                const activityId = getActivityId(activity)
                return (
                  <option key={activityId} value={activityId}>
                    {activity.activityName}
                  </option>
                )
              })}
            </SelectField>
          </div>
        )
      }}
    </ModalComponent>
  )
}

DateRangeFilterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dateRange: PropTypes.shape({
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    dateFilterType: PropTypes.string,
    datePreset: PropTypes.string,
    weekNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weekYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    blockGroupName: PropTypes.string,
    deviceGroupName: PropTypes.string,
    activityName: PropTypes.string
  }).isRequired,
  sectors: PropTypes.array,
  farmingBlocks: PropTypes.array,
  groups: PropTypes.array,
  activities: PropTypes.array,
  maxDate: PropTypes.string,
  title: PropTypes.string,
  applyLabel: PropTypes.string,
  showClearFilters: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
}

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
}
